import { AxiosResponse } from "axios"
import { apiInstance } from "../base"
import { appendArray, JournalExportFormat } from "../admin-audit-log"
import {
    IListPartnerApiEventsQuery,
    IListPartnerApiEventsResponse,
    IExportPartnerApiEventsQuery,
    IPartnerApiEventFilters,
    PartnerApiEvent,
} from "./types"

/**
 * Часть фильтров вводится вручную (id операций, ключей, целей), поэтому
 * значения обрезаются и дедуплицируются до отправки: пустая строка не должна
 * превращаться в фильтр, а повтор — в лишний элемент CSV-списка.
 */
const normalizeValues = (values?: string[]): string[] | undefined => {
    const normalized: string[] = []

    for (const value of values ?? []) {
        const trimmed = value.trim()
        if (trimmed && normalized.indexOf(trimmed) === -1) normalized.push(trimmed)
    }

    return normalized.length ? normalized : undefined
}

/**
 * Сервер принимает строгий allowlist query-ключей и отвечает 400 на любой
 * неизвестный ключ — в том числе на попытку передать tenant (`bot_id`).
 * Сообщество берётся только из JWT, поэтому здесь его нет и быть не может.
 */
const buildFilterParams = (
    filters: IPartnerApiEventFilters
): Record<string, string | number> => {
    const params: Record<string, string | number> = {}

    if (filters.periodFrom) params.period_from = filters.periodFrom
    // Конец периода отправляем выбранным: провайдер сам приводит date-only
    // значение к полуинтервалу. Предварительный сдвиг на клиенте дал бы лишний
    // день в списке и `export_limit_exceeded` на 31-дневном периоде.
    if (filters.periodTo) params.period_to = filters.periodTo

    appendArray(params, "event_types", normalizeValues(filters.eventTypes))
    appendArray(params, "record_kinds", normalizeValues(filters.recordKinds))
    appendArray(params, "outcomes", normalizeValues(filters.outcomes))
    appendArray(params, "operation_ids", normalizeValues(filters.operationIds))
    appendArray(params, "api_key_ids", normalizeValues(filters.apiKeyIds))
    appendArray(params, "target_types", normalizeValues(filters.targetTypes))
    appendArray(params, "target_ids", normalizeValues(filters.targetIds))

    return params
}

export const buildPartnerApiEventParams = (
    query: IListPartnerApiEventsQuery
): Record<string, string | number> => {
    const params = buildFilterParams(query)

    params.page = query.page
    params.page_size = query.pageSize
    // Страница > 1 без снимка отвергается как `partner_cursor_invalid`:
    // курсор переносится между страницами и сбрасывается при смене фильтров.
    if (query.snapshotCursor) params.snapshot_cursor = query.snapshotCursor

    return params
}

export const buildPartnerApiEventExportParams = (
    filters: IExportPartnerApiEventsQuery,
    format: JournalExportFormat
): Record<string, string | number> => {
    const params = buildFilterParams(filters)

    params.format = format

    return params
}

export const listPartnerApiEvents = (
    query: IListPartnerApiEventsQuery
): Promise<AxiosResponse<IListPartnerApiEventsResponse>> => {
    // Обработка истёкшей сессии — общая, как у соседнего `listAdminAuditLog`:
    // администратор должен получить обычное продление токена, а не остаться
    // с ошибкой на странице.
    return apiInstance.handler.get<IListPartnerApiEventsResponse>("/partner-api-events", {
        params: buildPartnerApiEventParams(query),
    })
}

export const getPartnerApiEvent = (
    eventId: string
): Promise<AxiosResponse<PartnerApiEvent>> => {
    return apiInstance.handler.get<PartnerApiEvent>(
        `/partner-api-events/${encodeURIComponent(eventId)}`
    )
}

export const exportPartnerApiEvents = (
    filters: IExportPartnerApiEventsQuery,
    format: JournalExportFormat
): Promise<AxiosResponse<Blob>> => {
    return apiInstance.handler.get<Blob>("/partner-api-events/export", {
        params: buildPartnerApiEventExportParams(filters, format),
        responseType: "blob",
    })
}
